/**
 * Decode the page target a coach manage-mode deep link carries.
 *
 * When a coach follows a link into one of their clients' accounts, the page they should land on
 * is stashed in session storage as a single string, because entering the client's account
 * restarts the app and only session storage survives that. This module turns that string back
 * into a page segment and its parameters.
 *
 * Several places write this format, so a change to it has to be traced to all of them:
 * `coach-push-deep-link.ts` builds it from a push notification's payload (the server side that
 * feeds it is `server/functions/src/helpers/coach-notify-payload.ts`);
 * `coach-client-detail.ts` writes the bare segment `intake-review` when the coach taps
 * "Review Intake"; `deepLinkPageForItem` in `coach-dashboard-helpers.ts` builds it for a coach
 * action item; and `server/functions/src/functions/coachNotify.ts` puts it in the `page` query
 * parameter of a notification email's link, which `processCoachManageAsync` then stashes.
 *
 * Kept apart from `coach-manage-deep-link.service.ts` on purpose: a plain module can be loaded
 * by the unit test runner, and an Angular service cannot. That is the same split the coach
 * invitation claim already uses for its own decisions (`coach-claim-failure-policy.ts`,
 * `coach-claim-reload-bridge.ts`).
 */

/** The part of `sessionStorage` that `takeStashedCoachDeepLinkPage` uses. */
export interface CoachDeepLinkPageStorage {
  getItem(key: string): string | null;
  removeItem(key: string): void;
}

/**
 * Take the stashed deep-link page out of storage, so it is opened exactly once.
 *
 * Answers null and leaves the stash where it is while an app reload is pending. The page that
 * stashed the link is the page being replaced: `processCoachManageAsync` stashes it and then
 * reloads, and the reload is not instant. If that old page finishes its own startup in the
 * meantime, it reaches the step that opens this page too. Every navigation is ignored once a
 * reload is pending, so the old page could not open it — and taking the stash anyway left the
 * reloaded app with nothing to open. The coach landed on the home page instead of the check-up
 * the link pointed at (nightly E2E run 34917906220, "Coach reviews a submitted weekly check-up").
 */
export function takeStashedCoachDeepLinkPage(
  storage: CoachDeepLinkPageStorage,
  key: string,
  reloadPending: boolean,
): string | null {
  // if a reload is coming, the reloaded app is the one that must open the page
  if (reloadPending) return null;

  const page = storage.getItem(key);
  if (!page) return null;
  storage.removeItem(key);
  return page;
}

/**
 * The page segments a CLIENT's own notification link may open.
 *
 * Today that is one link: the button in the "check-up reviewed" email, built in
 * `server/functions/src/functions/coachNotifyClient.ts`. Add a segment here when the server
 * starts sending a client a link to another page.
 */
const CLIENT_DEEP_LINK_SEGMENTS: readonly string[] = ['weekly-checkup'];

/**
 * Whether a `page` value from a link with NO `coachManage` may be opened (#6433).
 *
 * A client's notification link carries the page to open in the same `segment:key~value` form the
 * coach links use, and is decoded by the same parser. The value comes out of a link anybody can
 * write, so it is checked against a short list before anything is opened. Without the list such
 * a link could reach `intake-review`, which `parseCoachDeepLinkPage` opens in the coach's change
 * mode.
 *
 * The coach links are NOT put through this: a coach link first enters the client's account, and
 * the pages it may open there are the coach's own business.
 */
export function isClientDeepLinkPage(page: string | null | undefined): boolean {
  if (!page) return false;
  return CLIENT_DEEP_LINK_SEGMENTS.includes(parseCoachDeepLinkPage(page).segment);
}

/** A coach's notification link: the client account to enter, and the page to open there. */
export interface CoachNotificationLink {
  clientUid: string;
  page?: string;
}

/** What to do with a notification link that has just arrived. */
export enum LinkTiming {
  /** The link names a page it may not open. Nothing was remembered. */
  Refused = 'Refused',
  /** The app has not finished starting. The link was remembered and start-up acts on it. */
  AfterStart = 'AfterStart',
  /** The app has finished starting, so the caller acts on the link now. */
  Now = 'Now',
}

/**
 * The notification links waiting to be acted on, and the rules for when each may be (#6433).
 *
 * A link can reach the app before the app is ready for it. In a browser the page filter reads it
 * out of the address while the app is still starting. On a phone the operating system hands it
 * over, and when the link is what started the app that happens in the first moments of the boot,
 * before anybody's database is open. So a link is remembered here and acted on once the app has
 * started. Acting early on a coach's link was tried and does reach the right page, but the
 * client's nickname cannot be read yet, so the "Managing" banner shows a raw account id.
 *
 * Everything is held in memory. A coach's PAGE goes to session storage separately, because
 * entering a client's account restarts the app; nothing here has to survive a restart, and a
 * stored client page could open on some later start.
 *
 * A link that arrives while nobody is signed in stays remembered for as long as this page load
 * lasts. If the person then signs in without the app restarting, it is acted on for them. If the
 * sign-in restarts the app, as an emailed sign-in link does, it is gone.
 *
 * Kept free of Angular so the unit tests can drive it; `coach-manage-deep-link.service.ts` holds
 * one and supplies the navigation.
 */
export class PendingNotificationLinks {
  private clientPage: string | null = null;
  private coachLink: CoachNotificationLink | null = null;
  private appStarted = false;
  private enteringClientAccount = false;

  /**
   * Remember the page a CLIENT's own link named, and say when it may be opened.
   *
   * `Now` means the caller opens it straight away, by taking it back out with `takeClientPage`,
   * which is where the signed-in checks are.
   */
  rememberClientPage(page: string | null | undefined): LinkTiming {
    if (!isClientDeepLinkPage(page)) return LinkTiming.Refused;

    this.clientPage = page!;
    return this.appStarted ? LinkTiming.Now : LinkTiming.AfterStart;
  }

  /**
   * Say when a COACH's link may be acted on, remembering it if that is not yet.
   *
   * Never refused: which pages a coach may open inside a client's account is not decided here.
   */
  rememberCoachLink(link: CoachNotificationLink): LinkTiming {
    if (this.appStarted) return LinkTiming.Now;

    this.coachLink = link;
    return LinkTiming.AfterStart;
  }

  /** Record that the app has reached the point in its start-up where links are acted on. */
  markAppStarted(): void {
    this.appStarted = true;
  }

  /** Take the remembered coach link, so it is acted on exactly once. */
  takeCoachLink(): CoachNotificationLink | null {
    const link = this.coachLink;
    this.coachLink = null;
    return link;
  }

  /**
   * Take the remembered client page, so it is opened exactly once.
   *
   * The page is given up, not opened, when a reload is coming (this page cannot navigate, and
   * the link is lost with it) or when the signed-in person has no paid access (the Subscribe
   * page is then the right place to be).
   */
  takeClientPage(state: { reloadPending: boolean; hasPaidAccess: boolean }): string | null {
    const page = this.clientPage;
    this.clientPage = null;
    if (!page || state.reloadPending || !state.hasPaidAccess) return null;
    return page;
  }

  /**
   * Claim the right to enter a client's account, or answer false when somebody already has.
   *
   * A link that starts the phone app is handed over twice, once as the launch link and once as
   * an ordinary one. Without this the second pass would start a second reload on top of the
   * first. A reload that is already pending counts as "somebody already has".
   */
  tryBeginEnteringClientAccount(reloadPending: boolean): boolean {
    if (this.enteringClientAccount || reloadPending) return false;
    this.enteringClientAccount = true;
    return true;
  }

  /**
   * Give that right back. Normally the reload has replaced the page by now; when it did not
   * happen, a later link must still be able to try.
   */
  finishEnteringClientAccount(): void {
    this.enteringClientAccount = false;
  }
}

/** A page to open, decoded from the stashed deep-link string. */
export interface CoachDeepLinkPageTarget {
  /** The page's route segment, e.g. `workout` or `intake-review`. */
  segment: string;
  /** The navigation parameters to hand the page, e.g. `{ id: 'abc123' }`. */
  params: { [key: string]: string };
}

/**
 * Decode a stashed deep-link page string.
 *
 * The wire format is `segment` on its own, or `segment:key~value,key~value`. A pair with no `~`
 * in it is skipped — there is no key to file its text under, and guessing one would navigate
 * with a parameter nobody wrote.
 *
 * Callers still have to check that `segment` names a page that exists; this module knows nothing
 * about the page registry.
 */
export function parseCoachDeepLinkPage(page: string): CoachDeepLinkPageTarget {
  const colonIdx = page.indexOf(':');
  const segment = colonIdx >= 0 ? page.substring(0, colonIdx) : page;
  const paramStr = colonIdx >= 0 ? page.substring(colonIdx + 1) : '';

  const params: { [key: string]: string } = {};
  if (paramStr) {
    for (const part of paramStr.split(',')) {
      const tildeIdx = part.indexOf('~');
      if (tildeIdx >= 0) {
        params[part.substring(0, tildeIdx)] = part.substring(tildeIdx + 1);
      }
    }
  }

  // option B coach review (#3389): the intake-review deep link maps to the intake form, which
  // the coach should enter in tabbed change mode so they can jump between answer tabs. Injecting
  // mode='change' here makes the intake form's navParam-based mode detection run the right path.
  // The intake form ALSO derives change mode from isCoachManaging() in its own initializeAsync,
  // so this is belt-and-braces. A mode the caller already supplied is left alone.
  if (segment === 'intake-review' && !params.mode) {
    params.mode = 'change';
  }

  return { segment, params };
}
